/**
 * CvsiPortalClient — the single API wrapper both portal apps use (Doc §5.2 step 4, §12.1).
 *
 * It attaches the tenant-scoped bearer token to every request, targets the authenticated `/v1`
 * router, and normalizes failures into `CvsiError`. Pages never call `fetch` directly — they call
 * typed methods here, so auth, base URL, and error handling live in exactly one place.
 *
 * `tokenProvider` is async so the host app can refresh an expired session token transparently.
 */

import { normalizeCvsiError, CvsiError } from './errors';
import { API } from './routes';
import type {
  TenantOverview, ApiKeySummary, ApiKeyWithSecret, WebhookEndpoint, UsageSummary,
  BillingSummary, SupportTicket, AuditEntry, ListResult, SupportCategory, SupportSeverity, SupportStatus,
  ReportList, RideGroupList, RideGroupDetail, BuildReportInput, SecurityEvidence, CorridorSummary, CorridorDetail, CorridorForecast,
  MovementEventList, MovementEventListOptions, MovementEventSummary, ReasonCodeCatalog, AnalyticsSummary, RetentionStatus, TenantReviewQueue, MissionControlSupport,
  ActiveMovementPopulation, ApiKeyTelemetry, BillingLineItemList, TenantReviewActionResult, TenantReviewActionType,
  SafetyForecastReport, SafetyAnomalyReport,
} from './types';
import type { CustomerPortalRole } from './roles';

export interface CvsiClientOptions {
  /** Base URL including the version segment, e.g. https://api.cvsi.cruze.com/v1 */
  baseUrl: string;
  tokenProvider: () => Promise<string>;
}

export class CvsiPortalClient {
  private readonly baseUrl: string;
  private readonly tokenProvider: () => Promise<string>;

  constructor(opts: CvsiClientOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/$/, '');
    this.tokenProvider = opts.tokenProvider;
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    let res: Response;
    try {
      const token = await this.tokenProvider();
      res = await fetch(`${this.baseUrl}${path}`, {
        ...init,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(init.headers ?? {}),
        },
      });
    } catch (err) {
      throw new CvsiError('network', 0, err instanceof Error ? err.message : 'Network request failed');
    }
    if (!res.ok) throw await normalizeCvsiError(res);
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  private post<T>(path: string, body?: unknown, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(path, { method: 'POST', body: body == null ? undefined : JSON.stringify(body), headers });
  }

  // ── Tenant ──────────────────────────────────────────────────────────────────
  getTenant() { return this.request<TenantOverview>(API.tenant); }
  sendTenantVerificationEmail() { return this.post<{ tenantId: string; emailVerified: boolean; sent: boolean; message: string }>(API.tenantVerificationEmail); }
  getMissionControlSupport() { return this.request<MissionControlSupport>('/mission-control'); }
  getSafetyForecast(opts: { zoneIds?: string[]; routeIds?: string[]; start?: string; end?: string } = {}) {
    const q = new URLSearchParams();
    if (opts.zoneIds?.length) q.set('zoneIds', opts.zoneIds.join(','));
    if (opts.routeIds?.length) q.set('routeIds', opts.routeIds.join(','));
    if (opts.start) q.set('start', opts.start);
    if (opts.end) q.set('end', opts.end);
    const qs = q.toString();
    return this.request<SafetyForecastReport>(`${API.safetyForecast}${qs ? `?${qs}` : ''}`);
  }
  getSafetyAnomalies(threshold?: number) {
    return this.request<SafetyAnomalyReport>(`${API.safetyAnomalies}${threshold != null ? `?threshold=${encodeURIComponent(threshold)}` : ''}`);
  }
  suspendTenant(reason: string) { return this.post(API.tenantSuspend, { reason }); }
  reactivateTenant(reason?: string) { return this.post(API.tenantReactivate, { reason }); }
  listPortalUsers() {
    return this.request<{ items: Array<{ id: string; email: string; displayName: string | null; role: CustomerPortalRole; status: string; createdAt: string | null; lastLoginAt: string | null }> }>('/admin/users');
  }
  // ── API keys ──────────────────────────────────────────────────────────────────
  listKeys() { return this.request<{ keys: ApiKeySummary[] } | { items: ApiKeySummary[] } | ApiKeySummary[]>(API.keys); }
  getKeyTelemetry(keyId: string) { return this.request<ApiKeyTelemetry>(API.keyTelemetry(keyId)); }
  createKey(body: { mode?: 'live' | 'test'; scopes?: string[] }) { return this.post<ApiKeyWithSecret>(API.keys, body); }
  rotateKey(keyId: string) { return this.post<ApiKeyWithSecret>(API.keyRotate(keyId)); }
  revokeKey(keyId: string) { return this.post<{ keyId: string; status: string }>(API.keyRevoke(keyId)); }

  // ── Policies & branding ─────────────────────────────────────────────────────────
  getPolicies() { return this.request<Record<string, unknown>>(API.policies); }
  setPolicies(overrides: Record<string, unknown>) { return this.request(API.policies, { method: 'PUT', body: JSON.stringify({ overrides }) }); }
  getBranding() { return this.request<Record<string, unknown>>(API.branding); }
  setBranding(branding: Record<string, unknown>) { return this.request(API.branding, { method: 'PUT', body: JSON.stringify({ branding }) }); }

  // ── Webhooks ────────────────────────────────────────────────────────────────────
  listWebhooks() { return this.request<WebhookEndpoint[] | { items: WebhookEndpoint[] } | { endpoints: WebhookEndpoint[] }>(API.webhooks); }
  upsertWebhook(endpoint: Partial<WebhookEndpoint>) { return this.request<WebhookEndpoint>(API.webhooks, { method: 'PUT', body: JSON.stringify(endpoint) }); }
  deleteWebhook(id: string) { return this.request(API.webhook(id), { method: 'DELETE' }); }
  sendTestWebhook() { return this.post<{ delivered: boolean; status?: number }>(API.webhookTest); }

  // ── Usage, billing, audit ─────────────────────────────────────────────────────────
  getUsage() { return this.request<UsageSummary>(API.usage); }
  getBillingSummary() { return this.request<BillingSummary>(API.billingSummary); }
  getBillingLineItems(opts: { start?: string; end?: string; limit?: number; cursor?: string } = {}) {
    const q = new URLSearchParams();
    q.set('limit', String(opts.limit ?? 100));
    if (opts.start) q.set('start', opts.start);
    if (opts.end) q.set('end', opts.end);
    if (opts.cursor) q.set('cursor', opts.cursor);
    return this.request<BillingLineItemList>(`${API.billingLineItems}?${q.toString()}`);
  }
  getAuditLog(opts: { limit?: number; from?: string; to?: string; actor?: string; action?: string; target?: string; search?: string } = {}) {
    const q = new URLSearchParams();
    q.set('limit', String(opts.limit ?? 50));
    if (opts.from) q.set('from', opts.from);
    if (opts.to) q.set('to', opts.to);
    if (opts.actor) q.set('actor', opts.actor);
    if (opts.action) q.set('action', opts.action);
    if (opts.target) q.set('target', opts.target);
    if (opts.search) q.set('search', opts.search);
    return this.request<{ events: AuditEntry[]; count?: number; total?: number; truncated?: boolean }>(`${API.audit}?${q.toString()}`);
  }
  downloadAuditLog(opts: { from?: string; to?: string; actor?: string; action?: string; target?: string; search?: string } = {}) {
    const q = new URLSearchParams();
    for (const [key, value] of Object.entries(opts)) if (value) q.set(key, value);
    return this.downloadBinary(`${API.auditExport}?${q.toString()}`, 'cvsi-audit-current-view.csv');
  }
  getSecurityEvidence() { return this.request<SecurityEvidence>(API.securityEvidence); }
  verifyAudit(limit = 1000) { return this.request<{ tenantId: string; algorithm: string; chain: { ok: boolean; checked: number; brokenAtSeq: number | null } }>(`${API.auditVerify}?limit=${limit}`); }
  /** Retention policy + legal-hold status — Audit Center. Read-only; holds are staff-managed. */
  getRetentionStatus() { return this.request<RetentionStatus>(API.retentionStatus); }
  /** Flagged movement events pending a staff review decision — Audit Center. Read-only; decisions
   *  (clear/confirm/escalate) are a CVSI-staff action, not exposed to the customer portal. */
  getReviewQueue(limit = 50) { return this.request<TenantReviewQueue>(`${API.reviewQueue}?limit=${limit}`); }
  submitReviewAction(eventId: string, body: { action: TenantReviewActionType; note?: string }) {
    return this.post<TenantReviewActionResult>(API.reviewQueueAction(eventId), body);
  }

  // ── Reports ────────────────────────────────────────────────────────────────────
  listReports(opts: { limit?: number; start?: string; end?: string; cursor?: string } = {}) {
    const q = new URLSearchParams();
    q.set('limit', String(opts.limit ?? 50));
    if (opts.start) q.set('start', opts.start);
    if (opts.end) q.set('end', opts.end);
    if (opts.cursor) q.set('cursor', opts.cursor);
    return this.request<ReportList>(`${API.reports}?${q.toString()}`);
  }
  /** Rides in a date window, one row per ride (grouping all its verification checkpoints) — backs "Your reports". */
  listVerificationRecords(opts: { limit?: number; start?: string; end?: string; cursor?: string } = {}) {
    const q = new URLSearchParams();
    q.set('limit', String(opts.limit ?? 50));
    if (opts.start) q.set('start', opts.start);
    if (opts.end) q.set('end', opts.end);
    if (opts.cursor) q.set('cursor', opts.cursor);
    return this.request<RideGroupList>(`${API.reports}/rides?${q.toString()}`);
  }
  /** Full detail for one ride — every verification checkpoint plus a live cruze-rideshare lookup. */
  getRideGroupDetail(externalRef: string) {
    return this.request<RideGroupDetail>(`${API.reports}/rides/${encodeURIComponent(externalRef)}/detail`);
  }
  /** Chart-ready daily trend, risk distribution, top reason codes, and a bounded ride-metric
   *  sample (fare/distance/duration/tip from the mobile app) for the given window. */
  getAnalyticsSummary(opts: { start?: string; end?: string } = {}) {
    return this.request<AnalyticsSummary>(API.analyticsSummary(opts.start, opts.end));
  }
  buildReport(input: BuildReportInput) { return this.post<Record<string, unknown>>(API.reports, input); }
  getReport(id: string) { return this.request<Record<string, unknown>>(API.report(id)); }
  /** @deprecated The export route requires an `Authorization` header — a bare URL cannot carry it. Use `downloadReport`. */
  exportReportUrl(id: string, format: 'pdf' | 'csv' | 'json' | 'docx') { return `${this.baseUrl}${API.reportExport(id, format)}`; }
  /**
   * Authenticated report export. Fetches the export with the tenant bearer token and returns the
   * binary body + a suggested filename, so the caller can trigger a browser download. The previous
   * `<a href>` approach 401'd because an anchor navigation cannot send the Authorization header.
   */
  async downloadReport(id: string, format: 'pdf' | 'csv' | 'json' | 'docx'): Promise<{ blob: Blob; filename: string }> {
    return this.downloadBinary(API.reportExport(id, format), `report-${id}.${format}`);
  }
  /** Word (.docx) export of one ride's full detail (checkpoints + live ride lookup), with embedded
   *  chart images (outcome distribution, fare breakdown). */
  async downloadRideDetailExport(externalRef: string): Promise<{ blob: Blob; filename: string }> {
    return this.downloadBinary(API.rideDetailExport(externalRef), `cvsi-ride-${externalRef}.docx`);
  }
  private async downloadBinary(path: string, fallbackFilename: string): Promise<{ blob: Blob; filename: string }> {
    let res: Response;
    try {
      const token = await this.tokenProvider();
      res = await fetch(`${this.baseUrl}${path}`, { headers: { Authorization: `Bearer ${token}` } });
    } catch (err) {
      throw new CvsiError('network', 0, err instanceof Error ? err.message : 'Network request failed');
    }
    if (!res.ok) throw await normalizeCvsiError(res);
    const blob = await res.blob();
    const cd = res.headers.get('content-disposition') ?? '';
    const match = /filename="?([^"]+)"?/.exec(cd);
    return { blob, filename: match?.[1] ?? fallbackFilename };
  }

  // ── Support ────────────────────────────────────────────────────────────────────
  listSupportTickets(status?: SupportStatus, limit = 50) {
    const q = new URLSearchParams();
    if (status) q.set('status', status);
    q.set('limit', String(limit));
    return this.request<ListResult<SupportTicket>>(`${API.supportTickets}?${q.toString()}`);
  }
  createSupportTicket(body: { category: SupportCategory; severity: SupportSeverity; subject: string; description: string; requestId?: string; eventId?: string }) {
    return this.post<SupportTicket>(API.supportTickets, body);
  }
  getSupportTicket(id: string) { return this.request<SupportTicket>(API.supportTicket(id)); }
  addSupportMessage(id: string, body: string) { return this.post<{ id: string; added: boolean }>(API.supportTicketMessages(id), { body }); }
  setSupportStatus(id: string, status: SupportStatus) { return this.post<{ id: string; status: SupportStatus }>(API.supportTicketStatus(id), { status }); }

  // ── Movement events ───────────────────────────────────────────────────────────────
  listMovementEvents(input: number | MovementEventListOptions = 50) {
    const opts = typeof input === 'number' ? { limit: input } : input;
    const q = new URLSearchParams();
    q.set('limit', String(opts.limit ?? 50));
    if (opts.cursor) q.set('cursor', opts.cursor);
    if (opts.from) q.set('from', opts.from);
    if (opts.to) q.set('to', opts.to);
    if (opts.risk?.length) q.set('risk', opts.risk.join(','));
    if (opts.verification?.length) q.set('verification', opts.verification.join(','));
    if (opts.eventType?.length) q.set('eventType', opts.eventType.join(','));
    if (opts.reasonCode?.length) q.set('reasonCode', opts.reasonCode.join(','));
    if (opts.businessReference) q.set('businessReference', opts.businessReference);
    if (opts.search) q.set('search', opts.search);
    if (opts.groupByMovement) q.set('groupByMovement', 'true');
    return this.request<MovementEventList>(`${API.eventsMovement}?${q.toString()}`);
  }
  getMovementEvent(id: string) { return this.request<MovementEventSummary>(API.eventMovement(id)); }
  getActiveMovementPopulation(limit = 100) { return this.request<ActiveMovementPopulation>(`${API.activeMovements}?limit=${limit}`); }
  /** Published reason-code catalog (severity, remediation, plain-language example) — public, no scope required. */
  getReasonCatalog() { return this.request<ReasonCodeCatalog>(API.reasonCodes); }

  // ── Freight corridors (OS2) ──────────────────────────────────────────────────────
  listCorridors() { return this.request<CorridorSummary[]>(API.corridors); }
  getCorridor(id: string) { return this.request<CorridorDetail>(API.corridor(id)); }
  getCorridorForecast(id: string, horizons: string = '30,60') { return this.request<CorridorForecast[]>(API.corridorForecast(id, horizons)); }
  /** Download a corridor forecast as GeoJSON (ArcGIS) or CSV (Power BI/Tableau), with the tenant token. */
  async downloadCorridorForecast(id: string, format: 'geojson' | 'csv'): Promise<{ blob: Blob; filename: string }> {
    let res: Response;
    try {
      const token = await this.tokenProvider();
      res = await fetch(`${this.baseUrl}${API.corridorForecastExport(id, format)}`, { headers: { Authorization: `Bearer ${token}` } });
    } catch (err) {
      throw new CvsiError('network', 0, err instanceof Error ? err.message : 'Network request failed');
    }
    if (!res.ok) throw await normalizeCvsiError(res);
    return { blob: await res.blob(), filename: `corridor-${id}-forecast.${format === 'geojson' ? 'geojson' : 'csv'}` };
  }
}
